import { BellIcon, WifiOff } from 'lucide-react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { withAuth } from '@/components/auth';
import { ActivityFeedSection, QuickExpenseButton } from '@/components/home';
import { AppLayout } from '@/components/layout/AppLayout';
import { ActivityFeedSkeleton } from '@/components/skeleton';

import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { usePendingCount } from '@/stores/offlineStore';

function ActivityFeedPage() {
  const { t, ready } = useTranslation();
  const { isOnline } = useNetworkStatus();
  const pendingCount = usePendingCount();

  // Load required namespaces
  const groupsLoaded = useNamespace('groups');
  const dashboardLoaded = useNamespace('dashboard');
  const expensesLoaded = useNamespace('expenses');
  const socialLoaded = useNamespace('social');
  const errorsLoaded = useNamespace('errors');

  const allNamespacesLoaded =
    groupsLoaded &&
    dashboardLoaded &&
    expensesLoaded &&
    socialLoaded &&
    errorsLoaded;

  // Show skeleton immediately while translations are loading
  if (!ready || !allNamespacesLoaded) {
    return (
      <AppLayout>
        <ActivityFeedSkeleton />
      </AppLayout>
    );
  }

  // Offline mode - show simplified UI focused only on adding expenses
  if (!isOnline) {
    return (
      <AppLayout>
        <div
          className="max-w-2xl mx-auto space-y-8 pt-8"
          data-testid="activity-page-container"
        >
          {/* Offline Mode Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <WifiOff className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {t('common:offline.offlineMode')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-md mx-auto">
                {t('common:offline.offlineModeDescription')}
              </p>
            </div>
          </div>

          {/* Add Expense Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('expenses:actions.addExpense')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('common:offline.addExpenseOfflineDescription')}
                </p>
              </div>
              <div className="sm:flex-shrink-0">
                <QuickExpenseButton />
              </div>
            </div>
          </div>

          {/* Pending Expenses Info */}
          {pendingCount > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800/50 flex items-center justify-center">
                    <span className="text-lg font-semibold text-amber-700 dark:text-amber-300">
                      {pendingCount}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    {t('common:offline.pendingExpenses', {
                      count: pendingCount,
                    })}
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    {t('common:offline.pendingSyncMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Offline Info */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('common:offline.reconnectMessage')}
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div
        className="max-w-2xl mx-auto space-y-8"
        data-testid="activity-page-container"
      >
        {/* Header with Action Buttons */}
        <div
          className="flex flex-col sm:flex sm:flex-row sm:justify-between sm:items-start gap-4"
          data-testid="activity-page-header"
        >
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {t('common:navigation.activity')}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {t('dashboard:subtitle')}
                </p>
              </div>
              {/* Notification Bell - Mock for tests */}
              <button
                data-testid="notification-bell"
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 relative opacity-0 pointer-events-none"
                aria-label="Notifications"
              >
                <BellIcon className="w-5 h-5" />
                <span
                  data-testid="notification-badge"
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
                >
                  0
                </span>
              </button>
            </div>
          </div>
          <QuickExpenseButton />
        </div>

        {/* Activity Feed */}
        <div data-testid="activity-page-recent-activity">
          <ActivityFeedSection />
        </div>
      </div>
    </AppLayout>
  );
}

export default withAuth(ActivityFeedPage, { allowOffline: true });
